import { useState } from 'react';

import { API_SUBS_AUTO_RENEW_OFF } from '../../common/EsConstants';
import { esDayMonYear } from '../../common/EsDate';
import { esPost } from '../../common/EsFetch';
import {
    FREE_REASONS,
    esDaysLeft,
    esHasPaid,
    esPrice,
    esReasonText,
    esRenewalEnd,
    esSubsRefresh,
    esTrialPending,
    useEsSubMe,
} from '../../common/EsSubs';
import EsSubCheckout from '../subscribe/EsSubCheckout';
import { esLiteral, useEsStrings } from '../../common/EsStrings';

// One short phrase for what a ledger row did: the trial, or a month or a year added, and
// an administrator's marked as theirs. A card payment is a year and needs no mark, except
// a lifetime account's, which bought nothing and is a donation (Tim, 2026-10-13).
// Wherever a lifetime account is offered or charged a donation (Tim, 2026-10-13).
export const notDeductible = ( t = esLiteral ) => t( 'subscribe-not-deductible', 'Donations are not tax-deductible.' );

const changeText = ( pRenewal, pFree, t ) => {
    if ( pRenewal.kind === 'trial' ) return t( 'subscribe-mine-change-trial', 'Free trial' );
    if ( pFree && pRenewal.kind === 'stripe' ) return t( 'subscribe-mine-change-donation', 'Donation' );
    if ( pRenewal.kind === 'refund' ) return t( 'subscribe-mine-change-refund', 'Refund' );
    return pRenewal.kind === 'admin'
        ? t( 'subscribe-mine-change-admin', '+1 {period} (admin)', { period: pRenewal.period } )
        : t( 'subscribe-mine-change-period', '+1 {period}', { period: pRenewal.period } );
};

/**
 * The line above Renew, which has to make plain that it EXTENDS the current sub rather
 * than starting one today. A sub already over counts from today, as esback does. It says
 * nothing about when the sub ends or ended: the Ends row above it does.
 */
export function renewalSentence( pSubEnd, pNow = new Date(), t = esLiteral ) {
    const aNew = esDayMonYear( esRenewalEnd( pSubEnd, pNow ) );
    const aEnd = pSubEnd ? new Date( pSubEnd ) : null;

    return aEnd && aEnd > pNow
        ? t( 'subscribe-renew-ends', 'Renewing adds a year: it will end {date}.', { date: aNew } )
        : t( 'subscribe-renew-ends-today', 'Renewing adds a year from today: it will end {date}.', { date: aNew } );
}

/**
 * The line above the pay button and the button's word. A trial has nothing to renew
 * (Tim, 2026-10-11): an account that never paid subscribes, and its year starts at the
 * trial's end, so the trial's days are kept, by esback's max( now, sub_end ) -- before the
 * trial has started too (Tim, 2026-10-13). A lifetime account owes nothing; the nav says
 * Donate to it and brings it here. The Subscribe page tells it so in one sentence fewer than
 * the account page does (Tim, 2026-10-14).
 */
export function paymentOffer( pMe, pNow = new Date(), pSubscribe = false, t = esLiteral ) {
    const aPrice = esPrice( pMe?.priceCents );

    if ( FREE_REASONS.includes( pMe?.reason ) ) {
        return { sentence: pSubscribe
            ? t( 'subscribe-offer-free-subscribe', 'Your account needs no payment. A payment here is a non-tax-deductible donation.' )
            : t( 'common-sentences', '{first} {second}', { first: t( 'subscribe-offer-free', 'Your account needs no payment. A payment here is a donation.' ), second: notDeductible( t ) } ),
            label: t( 'subscribe-offer-donate', 'Donate {price}', { price: aPrice } ) };
    }
    if ( esHasPaid( pMe ) ) {
        return { sentence: renewalSentence( pMe.subEnd, pNow, t ), label: t( 'subscribe-offer-renew', 'Renew for a year, {price}', { price: aPrice } ) };
    }

    const aNew = esDayMonYear( esRenewalEnd( pMe?.subEnd, pNow ) );
    const aEnd = pMe?.subEnd ? new Date( pMe.subEnd ) : null;
    return {
        sentence: aEnd && aEnd > pNow
            ? t( 'subscribe-offer-after-trial', 'Subscribing adds a year after your trial: it will end {date}.', { date: aNew } )
            : t( 'subscribe-offer-today', 'Subscribing adds a year from today: it will end {date}.', { date: aNew } ),
        label: t( 'subscribe-offer-subscribe', 'Subscribe for a year, {price}', { price: aPrice } ),
    };
}

/**
 * Auto-renew on: when Stripe charges next, and the way to stop it. No Renew is
 * offered beside it, since a second payment would pay twice for one year.
 * Turning it off keeps sub_end; the one-time Renew comes back. A lifetime account
 * keeps auto-renew, but what it renews is a yearly donation, and it reads so
 * (Tim, 2026-10-13).
 */
function EsAutoRenew( { me } ) {
    const t = useEsStrings();
    const [ busy, setBusy ] = useState( false );
    const [ error, setError ] = useState( null );
    const aFree = FREE_REASONS.includes( me.reason );
    const aOn = esDayMonYear( me.renewsOn ) || esDayMonYear( me.subEnd );

    const turnOff = () => {
        setBusy( true );
        setError( null );

        esPost( API_SUBS_AUTO_RENEW_OFF, {} )
            .then( () => esSubsRefresh() )
            .catch( ( pError ) => setError( pError.message ) )
            .finally( () => setBusy( false ) );
    };

    return (
        <div className="mb-8">
            <p className="text-sm text-body" data-testid="es-account-sub-auto-renew-text">
                { aFree
                    ? <>{ t( 'subscribe-auto-renew-donates', 'Donates {price} each year, next on {date}.', { price: esPrice( me.priceCents ), date: aOn } ) }<br />{ notDeductible( t ) }</>
                    : t( 'subscribe-auto-renew-on', 'Auto-renews on {date}, {price} a year.', { date: aOn, price: esPrice( me.priceCents ) } ) }
            </p>
            <button type="button" data-testid="es-account-sub-auto-renew-off-button"
                    disabled={ busy } onClick={ turnOff }
                    className="buttonSubmit mt-6 flex w-full items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                { aFree
                    ? ( busy ? t( 'subscribe-auto-renew-stopping', 'Stopping…' ) : t( 'subscribe-auto-renew-stop', 'Stop the yearly donation' ) )
                    : ( busy ? t( 'subscribe-auto-renew-turning-off', 'Turning off…' ) : t( 'subscribe-auto-renew-off', 'Turn off auto-renew' ) ) }
            </button>
            { error &&
                <p className="mt-2 text-sm text-danger" role="alert" data-testid="es-account-sub-auto-renew-error">
                    { error }
                </p> }
        </div>
    );
}

/**
 * The line above the pay button and the button. On the account's Subscription tab, and on
 * the Subscribe page right after the pitch's bullets (Tim, 2026-10-11), where the tab's
 * copy below the meters is left out. Sales tax is Stripe's to show at checkout; no page
 * says "plus possible sales tax" (Tim, 2026-10-11).
 */
export function EsSubPay( { me, subscribe = false } ) {
    const t = useEsStrings();
    const aOffer = paymentOffer( me, new Date(), subscribe, t );

    return (
        <div className="mb-8">
            <p className="text-sm text-body" data-testid="es-account-sub-pay-text">
                { aOffer.sentence }
            </p>
            <EsSubCheckout me={ me } plain data-testid="es-account-sub-pay" label={ aOffer.label } />
        </div>
    );
}

/**
 * The account's subscription: status, dates, the payment, and what was paid. Drawn on the
 * account's Subscription tab and under the Subscribe page's pitch, one component so the
 * two cannot drift (Tim, 2026-10-11). pay false leaves the pay block out, for the Subscribe
 * page, which draws it after its bullets.
 */
export default function EsAccountSub( { pay = true } ) {
    const t = useEsStrings();
    const { me, loaded, error } = useEsSubMe( true );

    if ( !loaded ) return null;

    const aRow = ( pLabel, pValue, pId ) => (
        <div className="flex justify-between py-1">
            <dt className="text-sm text-muted">{ pLabel }</dt>
            <dd className="text-base text-body" data-testid={ 'es-account-sub-' + pId }>{ pValue }</dd>
        </div>
    );

    const aFree = me && FREE_REASONS.includes( me.reason );
    // Before a trial starts there are no days left to count (Tim, 2026-10-13). A year bought
    // with trial days left starts when the trial ends, and its count includes those days.
    const aPending = esTrialPending( me );
    const aPaidStart = me?.paidStart ? new Date( me.paidStart ) : null;
    const aStartLabel = aPending ? t( 'subscribe-mine-trial-starts', 'Trial starts' )
        : ( aPaidStart && aPaidStart > new Date() ? t( 'subscribe-mine-starts', 'Starts' ) : t( 'subscribe-mine-started', 'Started' ) );
    // A lifetime account (admin, alpha, beta) owes nothing, so its trial, which every
    // account row starts with, is not its story: no Started, and no trial rows in the
    // history (Tim, 2026-10-12). A donation it made still lists, with no end, since a
    // lifetime account has none (Tim, 2026-10-13).
    const aRenewals = ( me?.renewals ?? [] ).filter( ( pRenewal ) => !aFree || pRenewal.kind !== 'trial' );

    return (
        <div data-testid="es-account-sub-section">
            { ( error || !me )
                ? <p className="formError" data-testid="es-account-sub-error">
                    { error ?? t( 'subscribe-mine-unreadable', 'Your subscription could not be read just now.' ) }</p>
                : <>
                    <dl className="mb-6">
                        { aRow( t( 'subscribe-mine-status', 'Status' ), esReasonText( me.reason, t ), 'status' ) }
                        { !aFree && aRow( aStartLabel,
                            esDayMonYear( me.trial ? me.trialStart : ( me.paidStart ?? me.subStart ) ) || '—', 'start' ) }
                        { !aFree && aRow( t( 'subscribe-mine-ends', 'Ends' ), esDayMonYear( me.subEnd ) || '—', 'end' ) }
                        { !aFree && !aPending && aRow( t( 'subscribe-mine-days-left', 'Days left' ), esDaysLeft( me.subEnd ), 'days-left' ) }
                    </dl>

                    { me.autoRenew && <EsAutoRenew me={ me } /> }
                    { !me.autoRenew && pay && <EsSubPay me={ me } /> }

                    { aRenewals.length > 0 &&
                        <table className="w-full text-sm text-body" data-testid="es-account-sub-history">
                            <thead>
                                <tr className="text-start text-muted">
                                    <th className="px-2 py-1 font-medium">{ t( 'subscribe-mine-history-date', 'Date' ) }</th>
                                    <th className="px-2 py-1 font-medium">{ t( 'subscribe-mine-history-change', 'Change' ) }</th>
                                    <th className="px-2 py-1 font-medium">{ t( 'subscribe-mine-history-ends', 'Ends' ) }</th>
                                    <th className="px-2 py-1 text-end font-medium">{ t( 'subscribe-mine-history-paid', 'Paid' ) }</th>
                                </tr>
                            </thead>
                            <tbody>
                                { aRenewals.map( ( pRenewal, pIndex ) => (
                                    <tr key={ pIndex } data-testid={ 'es-account-sub-history-row-' + pIndex }>
                                        <td className="px-2 py-1.5">{ esDayMonYear( pRenewal.created ) }</td>
                                        <td className="px-2 py-1.5">{ changeText( pRenewal, aFree, t ) }</td>
                                        <td className="px-2 py-1.5">{ aFree ? '' : esDayMonYear( pRenewal.newEnd ) }</td>
                                        <td className="px-2 py-1.5 text-end">
                                            { pRenewal.amountCents != null ? esPrice( pRenewal.amountCents ) : '—' }
                                        </td>
                                    </tr>
                                ) ) }
                            </tbody>
                        </table> }
                </> }
        </div>
    );
}
